import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { PrimisCollateralControl } from '@/hooks/usePrimisCollateral';
import { WarehouseFieldStatus } from './WarehouseFieldStatus';

export function PrimisCollateralDate({ control, compact = false, disabled = false }: {
  control: PrimisCollateralControl; compact?: boolean; disabled?: boolean;
}) {
  const id = useId();
  const [invalid, setInvalid] = useState(false);
  const missing = !control.value && !control.isDirty;
  const metadata = control.snapshot?.primis_collateral_requested_set_by_name;
  const savedDate = control.source || 'Not recorded';
  const message = invalid ? 'Enter a complete, valid date.' : control.saving ? 'Saving collateral request date…'
    : control.error ? `${control.error}\nSaved date: ${savedDate}` : control.isDirty ? 'Unsaved collateral request date'
    : missing ? 'Record the date you asked Primis to send collateral. You can add it later.'
    : `Collateral request date saved${metadata ? ` by ${metadata}` : ''}.`;
  return (
    <div className="min-w-0" data-collateral-control data-row-interactive="true" onClick={event => event.stopPropagation()}>
      {!compact && <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">Primis collateral requested</label>}
      <div className="flex h-8 min-w-0 items-center gap-1">
        <Input id={id} type="date" aria-label="Primis collateral requested" aria-describedby={`${id}-description`}
          aria-invalid={invalid || !control.valid} value={control.value} disabled={disabled || control.blocked}
          onChange={event => {
            setInvalid(event.currentTarget.validity.badInput);
            control.setValue(event.target.value, event.currentTarget.validity.badInput);
          }}
          onBlur={event => {
            const valid = event.currentTarget.validity.valid && control.valid;
            setInvalid(!valid);
            if (compact && valid && control.isDirty) {
              void control.save();
            }
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              event.currentTarget.blur();
            }
          }}
          className={cn('h-8 min-w-0 flex-1 px-1 text-xs tabular-nums', missing && 'border-warning text-warning',
            (invalid || control.error) && 'border-destructive')}
        />
        <WarehouseFieldStatus label="Collateral request status" message={message}
          tone={control.saving ? 'saving' : control.error || invalid ? 'error' : missing || control.isDirty ? 'warning' : 'success'}>
          <div className="flex flex-wrap gap-2">
            {control.error && control.isDirty && <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
              disabled={control.saving || control.checking || !control.valid} onClick={() => void control.save(true)}>Save my date</Button>}
            {control.error && <Button type="button" size="sm" variant="ghost" className="h-8 text-xs"
              disabled={control.saving || control.checking} onClick={() => void control.check()}>Check again</Button>}
            {control.isDirty && <Button type="button" size="sm" variant="ghost" className="h-8 text-xs"
              disabled={control.saving} onClick={control.reset}>Use saved date</Button>}
          </div>
        </WarehouseFieldStatus>
      </div>
      <span id={`${id}-description`} className="sr-only">Date you asked Primis to send collateral. {missing ? 'Date not yet recorded.' : ''}</span>
    </div>
  );
}
